import { BaseStreamRegistry } from './base'
import { DEFAULT_RETRY_CONFIG } from '../config'
import { ENDPOINTS } from '@shared/utils/constant'
import { StreamKey, type StreamHandlers } from '../types'
import { TradeExecutionSide } from '@injectivelabs/sdk-ts/types'
import { StreamManagerV2 } from '@injectivelabs/sdk-ts/client/indexer'
import { IndexerGrpcSpotStreamV2 } from '@injectivelabs/sdk-ts/client/indexer'
import { IndexerGrpcArchiverStreamV2 } from '@injectivelabs/sdk-ts/client/indexer'
import { HELIX_ENDPOINTS } from '@/app/utils/constants'
import type {
  SpotOrdersStreamCallbackV2,
  SpotTradesStreamCallbackV2,
  SpotOrderHistoryStreamCallbackV2,
  SpotAverageEntriesStreamCallbackV2,
  SpotOrderbookUpdateStreamCallbackV2
} from '@injectivelabs/sdk-ts'

const spotMarketStreamV2 = new IndexerGrpcSpotStreamV2(ENDPOINTS.indexer)
const archiverStreamV2 = new IndexerGrpcArchiverStreamV2(
  HELIX_ENDPOINTS.archiver
)

export class SpotStreamRegistry extends BaseStreamRegistry {
  subscribeSpotOrderHistory(
    subaccountId: string,
    marketId: string | undefined,
    handlers: StreamHandlers<Parameters<SpotOrderHistoryStreamCallbackV2>[0]>
  ): void {
    const streamId = StreamKey.SpotOrderHistory

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return spotMarketStreamV2.streamOrderHistory({
          subaccountId,
          ...(marketId && { marketId }),
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })
    this.registerStream(streamId, manager, handlers)
  }

  subscribeSpotSubaccountTrades(
    subaccountId: string,
    marketId: string | undefined,
    handlers: StreamHandlers<Parameters<SpotTradesStreamCallbackV2>[0]>
  ): void {
    const streamId = StreamKey.SpotTradesSubaccount

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return spotMarketStreamV2.streamTrades({
          subaccountId,
          ...(marketId && { marketId }),
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })
    this.registerStream(streamId, manager, handlers)
  }

  subscribeSpotOrders(
    subaccountId: string,
    marketId: string | undefined,
    handlers: StreamHandlers<Parameters<SpotOrdersStreamCallbackV2>[0]>
  ): void {
    const streamId = StreamKey.SpotOrders

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return spotMarketStreamV2.streamOrders({
          subaccountId,
          ...(marketId && { marketId }),
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })
    this.registerStream(streamId, manager, handlers)
  }

  subscribeSpotOrderbook(
    marketId: string,
    handlers: StreamHandlers<Parameters<SpotOrderbookUpdateStreamCallbackV2>[0]>
  ): void {
    const streamId = StreamKey.Orderbook

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return spotMarketStreamV2.streamOrderbookUpdates({
          marketIds: [marketId],
          callback: (response) => {
            manager.emit('data', response)
          }
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })
    this.registerStream(streamId, manager, handlers)
  }

  subscribeSpotTrades(
    marketId: string,
    handlers: StreamHandlers<Parameters<SpotTradesStreamCallbackV2>[0]>
  ): void {
    const streamId = StreamKey.SpotTrades

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return spotMarketStreamV2.streamTrades({
          marketId,
          executionSide: TradeExecutionSide.Taker,
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })
    this.registerStream(streamId, manager, handlers)
  }

  subscribeSpotAverageEntries(
    account: string,
    handlers: StreamHandlers<Parameters<SpotAverageEntriesStreamCallbackV2>[0]>
  ): void {
    const streamId = StreamKey.SpotAverageEntries

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return archiverStreamV2.streamSpotAverageEntries({
          account,
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })
    this.registerStream(streamId, manager, handlers)
  }

  unsubscribeSpotSubaccountTrades(): void {
    this.unsubscribe(StreamKey.SpotTradesSubaccount)
  }

  unsubscribeSpotAverageEntries(): void {
    this.unsubscribe(StreamKey.SpotAverageEntries)
  }

  unsubscribeSpotOrderHistory(): void {
    this.unsubscribe(StreamKey.SpotOrderHistory)
  }

  unsubscribeSpotOrderbook(): void {
    this.unsubscribe(StreamKey.Orderbook)
  }

  unsubscribeSpotOrders(): void {
    this.unsubscribe(StreamKey.SpotOrders)
  }

  unsubscribeSpotTrades(): void {
    this.unsubscribe(StreamKey.SpotTrades)
  }
}
